import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import {
  Heart, Globe, Building2, User, ExternalLink, Loader2, ArrowLeft
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const API = process.env.REACT_APP_BACKEND_URL;

const TIER_CONFIG = {
  founding: { label: 'Founding Patron', color: 'bg-amber-100 text-amber-800 border-amber-300' },
  champion: { label: 'Champion', color: 'bg-purple-100 text-purple-800 border-purple-300' },
  sponsor: { label: 'Sponsor', color: 'bg-blue-100 text-blue-800 border-blue-300' },
  supporter: { label: 'Supporter', color: 'bg-green-100 text-green-800 border-green-300' },
};

const TYPE_ICONS = {
  individual: User,
  organization: Building2,
};

export default function PatronsPage() {
  const navigate = useNavigate();
  const [patrons, setPatrons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterTier, setFilterTier] = useState('all');

  useEffect(() => {
    fetchPatrons();
  }, []);

  const fetchPatrons = async () => {
    try {
      const res = await fetch(`${API}/api/patrons`);
      if (res.ok) setPatrons(await res.json());
    } catch (err) { /* silent */ }
    setLoading(false);
  };

  const filtered = filterTier === 'all' ? patrons : patrons.filter(p => p.tier === filterTier);
  const tiers = [...new Set(patrons.map(p => p.tier))];

  if (loading) {
    return (
      <div className="flex justify-center py-16">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-4 py-8" data-testid="patrons-page">
      <Button variant="ghost" size="sm" onClick={() => navigate(-1)} className="mb-4">
        <ArrowLeft className="h-4 w-4 mr-1" /> Back
      </Button>

      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900" data-testid="patrons-title">Our Patrons</h1>
        <p className="text-sm text-gray-600 mt-1">
          Thank you to the individuals and organizations that support our community.
        </p>
      </div>

      {patrons.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center text-muted-foreground">
            <Heart className="w-12 h-12 mx-auto mb-3 text-gray-300" />
            <p className="text-lg font-medium">No patrons yet</p>
            <p className="text-sm mt-1">Interested in supporting our community? Contact us!</p>
          </CardContent>
        </Card>
      ) : (
        <>
          {/* Tier filters */}
          {tiers.length > 1 && (
            <div className="flex gap-2 mb-6 flex-wrap" data-testid="patron-tier-filters">
              <button
                onClick={() => setFilterTier('all')}
                className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
                  filterTier === 'all' ? 'bg-gray-900 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                All ({patrons.length})
              </button>
              {tiers.map(tier => {
                const config = TIER_CONFIG[tier] || TIER_CONFIG.supporter;
                const count = patrons.filter(p => p.tier === tier).length;
                return (
                  <button
                    key={tier}
                    onClick={() => setFilterTier(tier)}
                    className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
                      filterTier === tier ? 'bg-gray-900 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    {config.label} ({count})
                  </button>
                );
              })}
            </div>
          )}

          {/* Patron cards */}
          <div className="grid gap-4 sm:grid-cols-2" data-testid="patron-list">
            {filtered.map(patron => {
              const tierConfig = TIER_CONFIG[patron.tier] || TIER_CONFIG.supporter;
              const TypeIcon = TYPE_ICONS[patron.type] || User;

              return (
                <Card key={patron.id} className="hover:shadow-md transition-shadow" data-testid={`patron-card-${patron.id}`}>
                  <CardContent className="p-4">
                    <div className="flex items-start gap-3">
                      {patron.logo_url ? (
                        <img src={patron.logo_url} alt={patron.name} className="w-12 h-12 rounded-lg object-cover shrink-0" />
                      ) : (
                        <div className="w-12 h-12 rounded-lg bg-gray-100 flex items-center justify-center shrink-0">
                          <TypeIcon className="w-6 h-6 text-gray-400" />
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <h3 className="font-semibold text-gray-900 truncate">{patron.name}</h3>
                          <Badge variant="outline" className={`text-[10px] shrink-0 ${tierConfig.color}`}>
                            {tierConfig.label}
                          </Badge>
                        </div>
                        {patron.tagline && (
                          <p className="text-sm text-gray-600 line-clamp-1">{patron.tagline}</p>
                        )}
                        {patron.public_message && (
                          <p className="text-xs text-gray-500 mt-1 line-clamp-2">{patron.public_message}</p>
                        )}
                        <div className="flex items-center gap-3 mt-2">
                          {patron.club_name && (
                            <span className="text-xs text-muted-foreground flex items-center gap-1">
                              <Globe className="w-3 h-3" /> {patron.club_name}
                            </span>
                          )}
                          {patron.website_url && (
                            <a
                              href={patron.website_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-xs text-blue-600 hover:text-blue-700 flex items-center gap-1"
                            >
                              <ExternalLink className="w-3 h-3" /> Website
                            </a>
                          )}
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}
